import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  FiservCardInterface,
  FiservCardLockedReasonsEnum,
  FiservCardStatusEnum,
  Hint,
  Inscription,
  LockIcon,
  Preloader,
  UnlockIcon,
  useResponsive,
} from '@common-fe/common-fe';

import { CardStatusActionWrap } from '../ChangeStatusCardModal.styles';

import CardActionCardHeader from './CardActionCardHeader';

const lockReasonTitles: Record<FiservCardLockedReasonsEnum, string> = {
  [FiservCardLockedReasonsEnum.REPAYMENT_REQUIRED]: 'Repayment required',
  [FiservCardLockedReasonsEnum.DOCUMENTATION_REQUIRED]: 'Documentation required',
  [FiservCardLockedReasonsEnum.FRAUD_DETECTED]: 'Fraud detected',
  [FiservCardLockedReasonsEnum.CARDHOLDERS_REQUEST]: 'Cardholders request',
};

const LOCKED_STATUSES = [FiservCardStatusEnum.FROZEN, FiservCardStatusEnum.PENDING_FROZEN];

interface CardCloseActionCardProps {
  card: FiservCardInterface;
  loadingButton: boolean;
  onOpenLockUnlockCardModal: () => void;
}

const CardLockUnlockActionCard: React.FC<CardCloseActionCardProps> = ({
  card,
  loadingButton,
  onOpenLockUnlockCardModal,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const isUnlockDisabled = useMemo(() => card?.employeeUnlockForbidden, [card]);
  const isCardLocked = useMemo(() => LOCKED_STATUSES.includes(card.status), [card.status]);

  const buttonLabel = useMemo(() => (isCardLocked ? t('Unlock Card') : t('Lock Card')), [isCardLocked, t]);

  const title = buttonLabel;
  const description = isMobile ? (
    t('You can unlock this card at any time.')
  ) : (
    <>
      {t('You can unlock this card')}
      <br />
      {t('at any time.')}
    </>
  );

  return (
    <Box width={isMobile ? '100%' : undefined}>
      <CardStatusActionWrap
        background="canvas"
        round="container1Round"
        $isMobile={isMobile}
        border={{ color: 'border2', size: 'small' }}
      >
        <CardActionCardHeader
          icon={
            isCardLocked ? (
              <UnlockIcon size="xlarge" color="iconAccent" />
            ) : (
              <LockIcon size="xlarge" color="iconAccent" />
            )
          }
          title={title}
          description={description}
          descriptionLineHeight="20px"
        />
        <Hint
          hintAlign="vertical"
          hintElement={
            <AppButton
              buttonType="primary"
              type="submit"
              width="100%"
              testId="card-lock-unlock-action-card_button"
              style={{ height: '40px', paddingInline: '12px' }}
              disabled={loadingButton || isUnlockDisabled}
              onClick={onOpenLockUnlockCardModal}
            >
              {loadingButton ? (
                <Box width="100%" align="center">
                  <Preloader color="white" />
                </Box>
              ) : (
                buttonLabel
              )}
            </AppButton>
          }
          hideHint={!isUnlockDisabled}
        >
          <Inscription size="small" weight={500} lineHeight="18px" textAlign="center">
            {t(
              `${card.lockReason ? `${lockReasonTitles[card.lockReason]}.` : ''} Card access cannot be modified at this time`
            )}
          </Inscription>
        </Hint>
      </CardStatusActionWrap>
    </Box>
  );
};

export default CardLockUnlockActionCard;
